import { CheckoutService } from './CheckoutService';
import { CartModel } from '../models/Cart.model';
import { SummaryModel } from '../models/Summary.model';
import { getPaymentAmount } from '../utils/getPaymentAmount';
import { getDiscountsToAlignWith } from '../utils/discounts.utils';
import { getOrderItemsFromCheckout } from '../utils/getOrderItemsFromCheckout';
import { getPaymentBreakdown } from '../utils/getPaymentBreakdown';
import { ECOM_PLATFORM_APP_DEF_ID } from '../utils/consts';
import {
  shouldNavigateToFastFlowCheckout,
  shouldNavigateToFastFlowCheckoutAfterPlaceOrderError,
} from '../utils/shouldNavigateToFastFlow';
import { NavigationService } from './NavigationService';
import { ExpressCheckoutSettingsService } from './ExpressCheckoutSettingsService';
import type { EcomPlatformViewerScriptContext } from '../../../../viewer-script/types';
import type { ExpressButtonsData, ExpressPaymentData } from '../types';
import { ExpressCheckoutButtonsState } from '../types';
import {
  ShippingContactRestricted,
  ShippingContactSelectedUpdate,
  ShippingError,
  ShippingMethod,
  ShippingMethodSelectedUpdate,
} from '@wix/cashier-express-checkout-widget/dist/src/types/Shipping';
import {
  OnPaymentAuthorizedResult,
  PaymentAuthorizedArgs,
} from '@wix/cashier-express-checkout-widget/dist/src/types/ExternalContract';
import { confirmPayment } from '@wix/cashier-express-checkout-widget/dist/src/utils/confirmPayment';
import {
  mapCashierAddressToEcomAddress,
  mapToCashierShippingAddress,
} from './cashierContactMapping';
import { ExpressCheckoutBIService } from './ExpressCheckoutBIService';
import { trackInitiateCheckout } from '../utils/analytics.utils';
import { hasShippableItems } from '../utils/hasShippableItems';

interface ServiceSessionState {
  checkoutId: string;
  latestTotalAmount: string;
  priceVerificationToken?: string;
  deliveryMethodCodeFromSummary?: string;
  numberOfShippingMethods: number;
  deliveryOptionsErrors?: {
    hasShippingZipCodeError?: boolean;
    hasShippingSubdivisionError?: boolean;
  };
}

export class ExpressCheckoutService {
  private readonly checkoutService: CheckoutService;
  private readonly settingsService: ExpressCheckoutSettingsService;
  private readonly context: EcomPlatformViewerScriptContext;
  private readonly expressCheckoutBIService: ExpressCheckoutBIService;

  private state: ServiceSessionState = {
    checkoutId: '',
    latestTotalAmount: '0',
    numberOfShippingMethods: 0,
  };

  static getInstance(
    context: EcomPlatformViewerScriptContext,
  ): ExpressCheckoutService {
    if (!context.expressCheckoutService) {
      context.expressCheckoutService = new ExpressCheckoutService(context);
    }
    return context.expressCheckoutService;
  }

  private constructor(context: EcomPlatformViewerScriptContext) {
    this.checkoutService = new CheckoutService({
      httpClient: context.platformServices!.essentials.httpClient,
      errorHandler: context.platformServices!.essentials.errorHandler,
      context,
      cartService: context.cartService,
      locale: context.siteStore.locale,
    });
    this.settingsService = ExpressCheckoutSettingsService.fromContext(context);
    this.context = context;
    this.expressCheckoutBIService = new ExpressCheckoutBIService(context);
  }

  public readonly onStateChange = ({
    state,
    numberOfButtons,
    origin,
  }: {
    state: ExpressCheckoutButtonsState;
    numberOfButtons?: number;
    origin?: string;
  }) => {
    if (state === ExpressCheckoutButtonsState.LOADING) {
      return;
    }
    this.expressCheckoutBIService.reportExpressCheckoutButtonsPresented({
      origin,
      numOfExpressButtonPresented: numberOfButtons,
    });
  };

  public readonly getButtonsData = async (callerContext: {
    widgetId: string;
  }): Promise<ExpressButtonsData> => {
    const settings = await this.settingsService.getSettings();
    const navigationService = new NavigationService(this.context, {});
    const viewMode = this.context.wixApi!.window.viewMode;
    const {
      appDefinitionId: appDefId,
      instanceId: appInstanceId,
      instance: appInstance,
    } = this.context.controllerConfigs[0].appParams ||
    /* istanbul ignore next */ {};
    const {
      metaSiteId,
      visitorId,
      ownerId: siteOwnerId,
    } = this.context.platformAPIs.bi || /* istanbul ignore next */ {};
    return {
      locale: this.context.siteStore.locale,
      domain: this.context.wixApi!.location.baseUrl,
      meta: {
        appDefId,
        appInstanceId,
        siteId: metaSiteId!,
        visitorId: visitorId!,
        siteOwnerId,
        appInstance,
        consumerWidgetId: callerContext.widgetId,
      },
      isExpressSupported: Boolean(settings.isExpressSupported),
      isPremium: settings.isPremium,
      canBusinessShip: settings.canBusinessShip,
      isOwner:
        siteOwnerId === this.context.wixApi!.user.currentUser.id ||
        Boolean(this.context.wixApi!.location.query.previewMode) ||
        viewMode === 'Preview',
      viewMode,
      isMobile: this.context.wixApi!.window.formFactor === 'Mobile',
      upgradeUrl: navigationService.upgradeUrl,
      shippingSettingsUrl: navigationService.shippingSettingsUrl,
    };
  };

  public readonly onCancel = () => {
    this.state = {
      ...this.state,
      numberOfShippingMethods: 0,
      deliveryOptionsErrors: undefined,
    };
  };

  public readonly getInitialPaymentData = async (
    checkoutId: string,
  ): Promise<ExpressPaymentData> => {
    const { cart, summary } =
      await this.checkoutService.getCheckoutByCheckoutId(checkoutId);
    this.context.wixApi!.window.trackEvent(...trackInitiateCheckout(cart));
    const paymentData = this.calculatePaymentData(cart, summary);
    this.state = {
      checkoutId,
      numberOfShippingMethods: 0,
      latestTotalAmount: paymentData.paymentAmount,
      priceVerificationToken: summary.priceVerificationToken,
      deliveryMethodCodeFromSummary: summary.deliveryMethodCodeFromSummary,
    };
    return paymentData;
  };

  public readonly onShippingContactSelected = async (
    shippingContact: ShippingContactRestricted,
  ): Promise<ShippingContactSelectedUpdate> => {
    try {
      const { cart, summary, shippingMethods, deliveryOptionsErrors } =
        await this.checkoutService.updateDeliveryAddress(
          this.state.checkoutId,
          mapToCashierShippingAddress(shippingContact),
        );

      if (shippingMethods && shippingMethods.length === 0) {
        return { error: ShippingError.SHIPPING_ADDRESS_UNSERVICEABLE };
      }

      const paymentData = this.calculatePaymentData(cart, summary);
      this.state = {
        ...this.state,
        latestTotalAmount: paymentData.paymentAmount,
        priceVerificationToken: summary.priceVerificationToken,
        deliveryMethodCodeFromSummary: summary.deliveryMethodCodeFromSummary,
        deliveryOptionsErrors,
        numberOfShippingMethods: shippingMethods?.length ?? 0,
      };

      return {
        ...paymentData,
        shippingMethods,
      };
    } catch {
      return { error: ShippingError.SHIPPING_ADDRESS_UNSERVICEABLE };
    }
  };

  public readonly onShippingMethodSelected = async (
    shippingMethod: ShippingMethod,
  ): Promise<ShippingMethodSelectedUpdate> => {
    const { cart, summary } = await this.checkoutService.setDeliveryMethod(
      this.state.checkoutId,
      shippingMethod.identifier,
    );

    const paymentData = this.calculatePaymentData(cart, summary);
    this.state = {
      ...this.state,
      latestTotalAmount: paymentData.paymentAmount,
      priceVerificationToken: summary.priceVerificationToken,
      deliveryMethodCodeFromSummary: summary.deliveryMethodCodeFromSummary,
    };

    return paymentData;
  };

  private readonly calculatePaymentData = (
    cart: CartModel,
    summary: SummaryModel,
  ): ExpressPaymentData => {
    const discountsToAlignWith = getDiscountsToAlignWith(cart, summary);
    return {
      orderItems: getOrderItemsFromCheckout(
        cart,
        summary,
        discountsToAlignWith,
      ),
      paymentBreakdown: getPaymentBreakdown(
        cart,
        summary,
        discountsToAlignWith,
      ),
      paymentAmount: getPaymentAmount(summary),
    };
  };

  public readonly onPaymentAuthorized = async (
    payment: PaymentAuthorizedArgs,
    shouldUpdateShippingAddress: boolean,
    {
      contentAppId,
      contentComponentId,
      successUrl,
      continueShoppingUrl,
      widgetId,
    }: {
      contentAppId?: string;
      contentComponentId?: string;
      successUrl?: string;
      continueShoppingUrl?: string;
      widgetId: string;
    },
  ): Promise<OnPaymentAuthorizedResult> => {
    const {
      checkoutId,
      latestTotalAmount,
      priceVerificationToken,
      deliveryOptionsErrors,
      numberOfShippingMethods,
      deliveryMethodCodeFromSummary,
    } = this.state;
    const navigationService = new NavigationService(this.context, {
      continueShoppingUrl,
      successUrl,
    });
    if (payment.error) {
      return { result: 'error' };
    }
    this.expressCheckoutBIService.clickContinueEWalletModalSf(checkoutId);

    let cart: CartModel;
    try {
      cart = await this.checkoutService.updateDeliveryAndBillingInfo(
        checkoutId,
        mapCashierAddressToEcomAddress(payment.billingContact),
        mapToCashierShippingAddress(
          payment.shippingContact,
          payment.billingContact,
        ),
        { shouldUpdateShippingAddress },
      );
    } catch {
      return { result: 'error' };
    }

    const shouldApplyDeliveryMethod =
      shouldUpdateShippingAddress &&
      hasShippableItems(cart.lineItems) &&
      numberOfShippingMethods === 1 &&
      deliveryMethodCodeFromSummary &&
      cart.deliveryMethodCode !== deliveryMethodCodeFromSummary;
    if (shouldApplyDeliveryMethod) {
      try {
        const result = await this.checkoutService.setDeliveryMethod(
          checkoutId,
          deliveryMethodCodeFromSummary,
        );
        cart = result.cart;
      } catch {
        return { result: 'error' };
      }
    }

    const paymentToken = payment.detailsId!;

    const expressCheckoutSettings = await this.settingsService.getSettings();
    if (
      await shouldNavigateToFastFlowCheckout({
        lineItems: cart.lineItems,
        checkoutId,
        purchaseFlowId: cart.purchaseFlowId,
        expressCheckoutSettings,
        checkoutService: this.checkoutService,
        biService: this.expressCheckoutBIService,
        isMemberLoggedIn: this.context.wixApi!.user.currentUser.loggedIn,
        deliveryOptionsErrors,
        shouldUpdateShippingAddress,
      })
    ) {
      await navigationService.navigateToCheckout(checkoutId, paymentToken);
      return { result: 'success' };
    }

    const redirectUrls = await this.getRedirectUrls(
      navigationService,
      checkoutId,
      paymentToken,
      contentAppId!,
      contentComponentId!,
    );
    let placeOrderResult: {
      orderId?: string;
      paymentResponseToken?: string | null;
    };
    try {
      const placeOrderParams = {
        id: checkoutId,
        paymentToken,
        redirectUrls,
        priceVerificationToken,
      };
      placeOrderResult = await this.checkoutService.placeOrder(
        placeOrderParams,
      );
    } catch (e) {
      if (
        shouldNavigateToFastFlowCheckoutAfterPlaceOrderError({
          error: e,
          checkoutId,
          purchaseFlowId: cart.purchaseFlowId,
          biService: this.expressCheckoutBIService,
        })
      ) {
        await navigationService.navigateToCheckout(checkoutId, paymentToken);
        return { result: 'success' };
      }
      return { result: 'error' };
    }

    const thankYouPageUrl = await navigationService.getThankYouPageUrl(
      placeOrderResult.orderId!,
    );
    const shouldNavigateToThankYouPage =
      !Number(latestTotalAmount) ||
      (await this.isPaymentConfirmed(placeOrderResult.paymentResponseToken, {
        widgetId,
      }));
    if (shouldNavigateToThankYouPage) {
      this.context.wixApi!.location.to?.(thankYouPageUrl);
    }

    return { result: 'success' };
  };

  private readonly isPaymentConfirmed = async (
    paymentResponseToken: string | null | undefined,
    { widgetId }: { widgetId: string },
  ) => {
    const result = await confirmPayment({
      chargeResponseToken: paymentResponseToken!,
      meta: {
        appDefId: ECOM_PLATFORM_APP_DEF_ID,
        msid: this.context.platformAPIs.bi?.metaSiteId as string,
        visitorId: this.context.platformServices!.bi.visitorId as string,
        widgetId,
      },
      biLoggerFactory: this.context.platformAPIs
        .biLoggerFactory as unknown as Parameters<
        typeof confirmPayment
      >[0]['biLoggerFactory'],
      redirectFn: this.context.wixApi!.location.to!,
    });
    return result.clientStatusV2 !== 'InProcess';
  };

  private readonly getRedirectUrls = async (
    navigationService: NavigationService,
    checkoutId: string,
    detailsId: string,
    contentAppId: string,
    contentComponentId: string,
  ) => {
    const thankYouPageUrl =
      await navigationService.getThankYouPageUrlForPlaceOrder();
    return {
      errorUrl: await navigationService.getCheckoutErrorUrl(
        checkoutId,
        detailsId,
        contentAppId,
        contentComponentId,
      ),
      cancelUrl: await navigationService.getCheckoutCancelUrl(
        checkoutId,
        detailsId,
        contentAppId,
        contentComponentId,
      ),
      successUrl: thankYouPageUrl,
      pendingUrl: thankYouPageUrl,
    };
  };
}
